/**
 * Redux slice for the gig-worker calendar view.
 *
 * Wraps GET /api/gig/calendar/{siteCd}?filter=all|today|thisweek.
 * Backend service impl is pending — currently returns []. UI should
 * tolerate an empty array.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_FETCH } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';
import { AvailableShiftRequest, GigFilter } from '../../types/gigApi';

interface GigCalendarState {
    shifts: AvailableShiftRequest[];
    loading: boolean;
    error: string | null;
    filter: GigFilter;
}

const initialState: GigCalendarState = {
    shifts: [],
    loading: false,
    error: null,
    filter: 'all',
};

/**
 * Fetch calendar data for all workers at a site.
 * Uses: GET /api/gig/calendar/{siteCd}?filter=all|today|thisweek
 */
export const fetchGigCalendar = createAsyncThunk(
    'gigCalendar/fetch',
    async (
        { siteCd, filter = 'all' }: { siteCd: string; filter?: GigFilter },
        { rejectWithValue }
    ) => {
        try {
            const params = new URLSearchParams({ filter });
            const response = await API_FETCH(
                `${ENDPOINTS.GIG_CALENDAR}/${encodeURIComponent(siteCd)}?${params.toString()}`
            );
            return Array.isArray(response) ? (response as AvailableShiftRequest[]) : [];
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Failed to fetch gig calendar');
        }
    }
);

const gigCalendarSlice = createSlice({
    name: 'gigCalendar',
    initialState,
    reducers: {
        setFilter: (state, action) => {
            state.filter = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchGigCalendar.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchGigCalendar.fulfilled, (state, action) => {
                state.loading = false;
                state.shifts = action.payload;
            })
            .addCase(fetchGigCalendar.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { setFilter, clearError } = gigCalendarSlice.actions;
export default gigCalendarSlice.reducer;
